import { useCallback, useMemo } from 'react';

import type { FormInstance } from 'antd/es/form';
import dayjs from 'dayjs';

import { SnapshotDataType } from 'types/snapshot';

import { CommunitySnapshotMatch, CompetitorSnapshotMatch, DataQuarterFilterFormValues, QuarterOption } from './types';

export const useGetSnapshotsGroupedByQuarter = (snapshots: SnapshotDataType[], communityId: number) =>
  useMemo(() => {
    if (!snapshots?.length) return [];

    const groupedByQuarter: Record<
      string,
      {
        communitySnapshot?: SnapshotDataType;
        competitorSnapshots: Record<number, SnapshotDataType>;
      }
    > = {};

    snapshots.forEach((snapshot: SnapshotDataType) => {
      const date = dayjs(snapshot.date);
      const quarter = `${date.year()}-Q${date.quarter()}`;
      const isCommunity = snapshot.community === communityId;

      if (!groupedByQuarter[quarter]) {
        groupedByQuarter[quarter] = { communitySnapshot: undefined, competitorSnapshots: {} };
      }

      if (isCommunity) {
        const existing = groupedByQuarter[quarter].communitySnapshot;
        if (!existing || dayjs(snapshot.date).isAfter(existing.date)) {
          groupedByQuarter[quarter].communitySnapshot = snapshot;
        }
      } else {
        const competitorId = snapshot.community;
        const existing = groupedByQuarter[quarter].competitorSnapshots[competitorId];
        if (!existing || dayjs(snapshot.date).isAfter(existing.date)) {
          groupedByQuarter[quarter].competitorSnapshots[competitorId] = snapshot;
        }
      }
    });

    return Object.entries(groupedByQuarter)
      .map(([slug, data]) => {
        const [year, quarter] = slug.split('-');
        return {
          slug,
          label: `${quarter} ${year}`,
          communitySnapshot: data.communitySnapshot,
          competitorSnapshots: Object.values(data.competitorSnapshots),
        };
      })
      .sort((a, b) => (a.slug < b.slug ? 1 : -1));
  }, [snapshots, communityId]);

type UseResolveQuarterSnapshotsArgs = {
  quarters: QuarterOption[];
  selectedQuarterSlug: string | null;
  competitors?: Array<{ id: number }>;
};

export const useResolveQuarterSnapshots = ({
  quarters,
  selectedQuarterSlug,
  competitors,
}: UseResolveQuarterSnapshotsArgs) => {
  if (!quarters.length) {
    return {
      selectedQuarterSlug: null,
      communityMatch: undefined,
      competitorMatches: [],
    };
  }

  const fallbackSlug = selectedQuarterSlug ?? quarters[0].slug ?? null;
  if (!fallbackSlug) {
    return {
      selectedQuarterSlug: null,
      communityMatch: undefined,
      competitorMatches: [],
    };
  }

  let startingQuarterIndex = quarters.findIndex((quarter) => quarter.slug === fallbackSlug);
  if (startingQuarterIndex === -1) {
    startingQuarterIndex = 0;
  }

  const findCommunityMatch = (): CommunitySnapshotMatch | undefined => {
    for (let index = startingQuarterIndex; index < quarters.length; index += 1) {
      const quarter = quarters[index];
      if (quarter.communitySnapshot) {
        return {
          snapshot: quarter.communitySnapshot,
          quarterSlug: quarter.slug,
          isCurrentQuarter: index === startingQuarterIndex,
        };
      }
    }
    return undefined;
  };

  const competitorIdsInOrder = [...(competitors?.map((competitor) => competitor.id) ?? [])];

  const competitorIdsSet = new Set<number>(competitorIdsInOrder);
  quarters.forEach((quarter) => {
    quarter.competitorSnapshots.forEach((snapshot) => {
      if (!competitorIdsSet.has(snapshot.community)) {
        competitorIdsSet.add(snapshot.community);
      }
    });
  });

  const orderedCompetitorIds = [
    ...competitorIdsInOrder,
    ...Array.from(competitorIdsSet).filter((id) => !competitorIdsInOrder.includes(id)),
  ];

  const competitorMatches: CompetitorSnapshotMatch[] = orderedCompetitorIds.reduce((accumulator, competitorId) => {
    for (let index = startingQuarterIndex; index < quarters.length; index += 1) {
      const candidate = quarters[index].competitorSnapshots.find((snapshot) => snapshot.community === competitorId);

      if (candidate) {
        accumulator.push({
          competitorId,
          snapshot: candidate,
          quarterSlug: quarters[index].slug,
          isCurrentQuarter: index === startingQuarterIndex,
        });
        return accumulator;
      }
    }

    return accumulator;
  }, [] as CompetitorSnapshotMatch[]);

  return {
    communityMatch: findCommunityMatch(),
    competitorMatches,
  };
};

export const useResetCallback = (form: FormInstance<DataQuarterFilterFormValues>, quarters: QuarterOption[]) =>
  useCallback(() => {
    form.resetFields();
    if (quarters.length) {
      form.setFieldsValue({
        selectedQuarterSlug: quarters[0].slug,
        includePriorCompetitors: true,
      });
    }
  }, [form, quarters]);
